'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { BlogCard } from '../blog/blog-card';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Carousel } from '../ui/carousel';
import { CarouselProgress } from '../ui/carousel-progress';

export function Blogs({
  blogs,
  title,
  description,
  className,
}: {
  blogs: Blog[];
  title: string;
  description?: string;
  className?: {
    section?: string;
    container?: string;
  };
}) {
  const { t } = useTranslations();

  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn(
        'custom-section',
        'section-gap',
        'py-8 pr-0 sm:py-10 md:pr-4 lg:py-12',
        className?.section,
      )}
    >
      <div className="flex flex-col items-center justify-center gap-4 pr-4 sm:gap-5 md:pr-0 lg:gap-6">
        <Heading center title={title} />
        {!!description && (
          <Paragraph className="w-full text-center" color="muted-foreground">
            {description}
          </Paragraph>
        )}
      </div>
      <div
        className={cn('custom-container', 'hidden grid-cols-3 gap-3 lg:grid', className?.container)}
      >
        {blogs.map(blog => (
          <BlogCard blog={blog} key={blog.slug} />
        ))}
      </div>
      <div className="block w-full lg:hidden">
        <Carousel
          options={options}
          className={{
            wrapper: 'w-full',
            main: 'w-full',
            container: 'w-full gap-3',
            slide: 'w-full max-w-[320px] sm:max-w-[360px] md:max-w-[400px]',
          }}
          slides={blogs.map(blog => (
            <BlogCard blog={blog} key={blog.slug} />
          ))}
        />
      </div>
      <CarouselProgress
        totalSlides={blogs.length}
        selectedIndex={selectedIndex}
        className={{
          container: 'flex w-full max-w-[221px] bg-primary/15 sm:max-w-[450px] lg:hidden',
          activeBar: 'bg-primary',
        }}
      />
      <div className="flex w-full items-center justify-center pr-4 md:pr-0">
        <Button asChild className="w-full sm:w-fit">
          <Link prefetch={false} href={ROUTES.BLOG()}>
            {t('text474')}
          </Link>
        </Button>
      </div>
    </section>
  );
}
